import {
  BrainCircuit,
  Building2,
  CheckCircle2,
  Cloud,
  Code2,
  Database,
  Globe2,
  Handshake,
  LockKeyhole,
  Rocket,
  Target,
  Workflow,
} from "lucide-react";

import FAQSection from "./FAQSection";

const highlights = [
  {
    icon: Target,
    title: "Our Mission",
    description:
      "To simplify business operations through practical SaaS products, intelligent automation and scalable software solutions that help organizations work more efficiently and grow with confidence.",
  },
  {
    icon: Globe2,
    title: "Our Vision",
    description:
      "To build a trusted ecosystem of cloud-based business software that enables organizations to manage people, customers, transactions, data and operations through connected digital platforms.",
  },
  {
    icon: Workflow,
    title: "How We Build",
    description:
      "We follow a structured product and engineering process covering business analysis, planning, UI/UX, development, testing, deployment, monitoring and continuous improvement.",
  },
  {
    icon: Handshake,
    title: "Long-Term Partnership",
    description:
      "Our work continues beyond deployment. We support businesses with maintenance, product improvements, integrations, optimization and scalable technology as their requirements evolve.",
  },
];

const capabilities = [
  {
    icon: Code2,
    title: "SaaS Engineering",
    text: "Scalable cloud-based software products designed around real business workflows.",
  },
  {
    icon: BrainCircuit,
    title: "AI & Automation",
    text: "Intelligent automation that helps reduce repetitive work and improve operational efficiency.",
  },
  {
    icon: Database,
    title: "Business Systems",
    text: "Connected ERP, CRM, HRMS, billing, reporting and data-driven business platforms.",
  },
  {
    icon: LockKeyhole,
    title: "Secure Architecture",
    text: "Security-conscious architecture with scalable systems, controlled access and reliable infrastructure.",
  },
];

export default function About() {
  return (
    <>
      <section className="relative overflow-hidden px-4 py-10 sm:px-6 lg:py-20">
        <div className="pointer-events-none absolute left-1/2 top-20 -z-10 h-[500px] w-[900px] -translate-x-1/2 rounded-full bg-violet-600/[0.08] blur-[160px]" />

        <div className="mx-auto max-w-7xl">
          <div className="grid gap-14 lg:grid-cols-2 lg:items-center">
            <div>
              <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-violet-400/20 bg-violet-400/5 px-4 py-2">
                <Building2 className="h-4 w-4 text-violet-300" />

                <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-violet-200">
                  About OjMee Technology
                </span>
              </div>

              <h1 className="text-3xl font-black leading-tight sm:text-5xl lg:text-6xl">
                Building SaaS & Business Software
                <span className="bg-gradient-to-r from-pink-400 via-fuchsia-400 to-violet-400 bg-clip-text text-transparent">
                  {" "}
                  for Modern Businesses
                </span>
              </h1>

              <p className="mt-6 text-sm leading-8 text-slate-400 sm:text-base">
                OjMee Technology is a SaaS and business software company
                focused on building cloud-based products, intelligent
                automation and scalable digital solutions that simplify
                everyday business operations.
              </p>

              <p className="mt-4 text-sm leading-8 text-slate-500 sm:text-base">
                We develop software for managing employees, customers,
                billing, invoicing, business workflows, reporting, remote
                operations and other essential processes. Our approach is
                centered on understanding how a business actually works
                before designing the technology around it.
              </p>

              <p className="mt-4 text-sm leading-8 text-slate-500 sm:text-base">
                Alongside our own SaaS products, we also design and develop
                custom web applications, mobile applications, ERP and CRM
                systems, APIs, cloud platforms and AI-powered automation for
                organizations with specialized requirements.
              </p>

              <div className="mt-8 flex flex-wrap gap-3">
                {[
                  "SaaS Products",
                  "Business Software",
                  "AI Automation",
                  "Cloud Solutions",
                ].map((item) => (
                  <div
                    key={item}
                    className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.035] px-4 py-2 text-xs font-semibold text-slate-300"
                  >
                    <CheckCircle2 className="h-3.5 w-3.5 text-pink-400" />
                    {item}
                  </div>
                ))}
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              {capabilities.map((item) => {
                const Icon = item.icon;

                return (
                  <div
                    key={item.title}
                    className="group relative overflow-hidden rounded-[2rem] border border-white/10 bg-white/[0.025] p-6 transition duration-300 hover:-translate-y-1 hover:border-pink-400/25 hover:bg-white/[0.04]"
                  >
                    <div className="pointer-events-none absolute -right-14 -top-14 h-32 w-32 rounded-full bg-pink-500/[0.07] blur-3xl opacity-0 transition duration-500 group-hover:opacity-100" />

                    <div className="relative">
                      <div className="grid h-12 w-12 place-items-center rounded-2xl bg-gradient-to-br from-pink-500/15 to-violet-500/15">
                        <Icon className="h-5 w-5 text-pink-300" />
                      </div>

                      <h2 className="mt-5 text-lg font-black">
                        {item.title}
                      </h2>

                      <p className="mt-2 text-sm leading-6 text-slate-500">
                        {item.text}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <section className="relative w-full border-y border-white/5 bg-white/[0.015] px-4 py-16 sm:px-6 lg:py-20">
        <div className="pointer-events-none absolute left-1/2 top-1/2 -z-10 h-[500px] w-full -translate-x-1/2 -translate-y-1/2 rounded-full bg-pink-600/[0.08] blur-[150px]" />

        <div className="mx-auto max-w-7xl">
          <div className="grid gap-12 lg:grid-cols-[0.8fr_1.2fr] lg:items-center">
            <div>
              <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-pink-400/20 bg-pink-400/5 px-4 py-2">
                <Cloud className="h-4 w-4 text-pink-300" />

                <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-pink-200">
                  SaaS-First Approach
                </span>
              </div>

              <h2 className="text-3xl font-black leading-tight sm:text-5xl">
                Software Designed to
                <span className="bg-gradient-to-r from-pink-400 to-violet-400 bg-clip-text text-transparent">
                  {" "}
                  Grow With Your Business
                </span>
              </h2>

              <p className="mt-6 text-sm leading-8 text-slate-400 sm:text-base">
                Modern businesses need software that can adapt as teams,
                customers, transactions and operational requirements grow.
                We build SaaS platforms with scalability, accessibility and
                long-term usability in mind.
              </p>

              <p className="mt-4 text-sm leading-8 text-slate-500 sm:text-base">
                Instead of treating software as a one-time implementation,
                we focus on creating digital platforms that can continuously
                improve through new features, integrations, automation and
                evolving business workflows.
              </p>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              {[
                {
                  icon: Cloud,
                  title: "Cloud-Based",
                  text: "Access business software through modern cloud-connected platforms.",
                },
                {
                  icon: Workflow,
                  title: "Workflow Driven",
                  text: "Software designed around practical business processes and everyday operations.",
                },
                {
                  icon: Rocket,
                  title: "Built to Scale",
                  text: "Architecture designed to evolve as users, data and operational requirements increase.",
                },
                {
                  icon: BrainCircuit,
                  title: "Automation Ready",
                  text: "AI and automation capabilities that can reduce manual work and improve efficiency.",
                },
              ].map((item) => {
                const Icon = item.icon;

                return (
                  <div
                    key={item.title}
                    className="rounded-[2rem] border border-white/10 bg-white/[0.025] p-6 transition duration-300 hover:-translate-y-1 hover:border-violet-400/25"
                  >
                    <div className="grid h-11 w-11 place-items-center rounded-xl bg-violet-500/10">
                      <Icon className="h-5 w-5 text-violet-300" />
                    </div>

                    <h3 className="mt-5 text-lg font-black">
                      {item.title}
                    </h3>

                    <p className="mt-2 text-sm leading-6 text-slate-500">
                      {item.text}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <section className="relative w-full overflow-hidden px-4 py-16 sm:px-6 lg:py-20">
        <div className="pointer-events-none absolute left-1/2 top-1/2 -z-10 h-[500px] w-full -translate-x-1/2 -translate-y-1/2 rounded-full bg-pink-600/10 blur-[150px]" />

        <div className="mx-auto max-w-7xl">
          <div className="mx-auto max-w-3xl text-center">
            <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-pink-400/20 bg-pink-400/5 px-4 py-2">
              <Target className="h-4 w-4 text-pink-300" />

              <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-pink-200">
                What Defines Us
              </span>
            </div>

            <h2 className="text-3xl font-black sm:text-5xl">
              The Principles Behind{" "}
              <span className="bg-gradient-to-r from-pink-400 to-violet-400 bg-clip-text text-transparent">
                Our Technology
              </span>
            </h2>

            <p className="mt-5 text-sm leading-7 text-slate-500 sm:text-base">
              We combine product thinking, software engineering and business
              understanding to create technology that solves practical
              operational challenges and supports long-term growth.
            </p>
          </div>

          <div className="mt-14 grid gap-5 sm:grid-cols-2">
            {highlights.map((item) => {
              const Icon = item.icon;

              return (
                <div
                  key={item.title}
                  className="group relative overflow-hidden rounded-[2rem] border border-white/10 bg-white/[0.025] p-7 transition duration-300 hover:-translate-y-1 hover:border-pink-400/25"
                >
                  <div className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full bg-pink-500/10 opacity-0 blur-[70px] transition duration-500 group-hover:opacity-100" />

                  <div className="relative">
                    <div className="flex items-start justify-between">
                      <div className="grid h-12 w-12 place-items-center rounded-2xl bg-gradient-to-br from-pink-500/15 to-violet-500/15">
                        <Icon className="h-5 w-5 text-pink-300" />
                      </div>

                      <CheckCircle2 className="h-5 w-5 text-emerald-400/60" />
                    </div>

                    <h3 className="mt-6 text-xl font-black">
                      {item.title}
                    </h3>

                    <p className="mt-3 text-sm leading-7 text-slate-500">
                      {item.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="relative w-full border-y border-white/5 bg-white/[0.015] px-4 py-16 sm:px-6 lg:py-20">
        <div className="mx-auto max-w-7xl">
          <div className="mx-auto max-w-4xl text-center">
            <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-violet-400/20 bg-violet-400/5 px-4 py-2">
              <Building2 className="h-4 w-4 text-violet-300" />

              <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-violet-200">
                Our Technology Focus
              </span>
            </div>

            <h2 className="text-3xl font-black leading-tight sm:text-5xl">
              One Technology Company.
              <br />

              <span className="bg-gradient-to-r from-pink-400 via-fuchsia-400 to-violet-400 bg-clip-text text-transparent">
                A Connected Software Ecosystem.
              </span>
            </h2>

            <p className="mx-auto mt-6 max-w-3xl text-sm leading-8 text-slate-400 sm:text-base">
              Our objective is to create a connected ecosystem where
              businesses can use technology to manage operations, employees,
              customers, transactions, reporting and digital workflows more
              effectively.
            </p>

            <p className="mx-auto mt-4 max-w-3xl text-sm leading-8 text-slate-500 sm:text-base">
              Businesses can start with an individual OjMee product or work
              with our team to develop a customized solution that integrates
              with their existing systems and operational processes.
            </p>

            <div className="mt-10 flex flex-wrap justify-center gap-3">
              {[
                "ERP",
                "CRM",
                "HRMS",
                "Billing & Invoicing",
                "Remote Access",
                "Business Analytics",
                "AI Automation",
                "Cloud APIs",
                "Custom SaaS",
              ].map((item) => (
                <div
                  key={item}
                  className="rounded-full border border-white/10 bg-white/[0.035] px-4 py-2.5 text-xs font-bold text-slate-400 transition hover:border-pink-400/25 hover:text-pink-300"
                >
                  {item}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="relative mx-auto w-full overflow-hidden px-4 py-5 sm:px-6 lg:w-[90%]">
        <FAQSection />
      </section>
    </>
  );
}